import { FormEvent, useState } from 'react';
import { AdminRole, CreateAdminRequest } from '../../types/admin-roles';

interface CreateAdminDialogProps {
  roles: AdminRole[];
  onCreate: (body: CreateAdminRequest) => Promise<void>;
  onClose: () => void;
}

const MIN_PASSWORD_LENGTH = 12;

export default function CreateAdminDialog({ roles, onCreate, onClose }: CreateAdminDialogProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [superAdmin, setSuperAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleRole = (id: string) =>
    setRoleIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  const canSubmit =
    firstName.trim() && lastName.trim() && email.trim() && password.length >= MIN_PASSWORD_LENGTH;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onCreate({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        password,
        roleIds,
        superAdmin,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the admin');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true">
      <form className="admin-modal" onSubmit={submit}>
        <div className="admin-modal-header"><h3>Add admin</h3></div>

        <div className="admin-modal-body">
          <label className="admin-field">
            <span className="admin-field-label">First name</span>
            <input className="admin-input" value={firstName}
                   onChange={(e) => setFirstName(e.target.value)} required autoFocus />
          </label>

          <label className="admin-field">
            <span className="admin-field-label">Last name</span>
            <input className="admin-input" value={lastName}
                   onChange={(e) => setLastName(e.target.value)} required />
          </label>

          <label className="admin-field">
            <span className="admin-field-label">Email</span>
            <input className="admin-input" type="email" value={email}
                   onChange={(e) => setEmail(e.target.value)} required />
          </label>

          <label className="admin-field">
            <span className="admin-field-label">Password</span>
            <input className="admin-input" type="password" value={password}
                   onChange={(e) => setPassword(e.target.value)}
                   minLength={MIN_PASSWORD_LENGTH} required />
            <span className="admin-text-muted">
              At least {MIN_PASSWORD_LENGTH} characters. Share it with them directly — this is the
              password they will keep signing in with, so treat it accordingly.
            </span>
          </label>

          <div className="admin-field">
            <span className="admin-field-label">Roles</span>
            {roles.length === 0 ? (
              <p className="admin-text-muted">No roles exist yet. Create one first, or grant super admin.</p>
            ) : (
              <div className="admin-roles-checkbox-list">
                {roles.map((role) => (
                  <label key={role.id} className="admin-roles-checkbox-item">
                    <input
                      type="checkbox"
                      checked={roleIds.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                      disabled={superAdmin}
                    />
                    <div>
                      <div className="admin-roles-checkbox-label">{role.name}</div>
                      <div className="admin-text-muted">
                        {role.description || `${role.screenKeys.length} screen(s)`}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <label className="admin-roles-checkbox-item">
            <input type="checkbox" checked={superAdmin} onChange={() => setSuperAdmin((v) => !v)} />
            <div>
              <div className="admin-roles-checkbox-label">Super Admin</div>
              <div className="admin-text-muted">
                Full access to every screen, including role management. Overrides any roles above.
              </div>
            </div>
          </label>

          {error && <div className="admin-error">{error}</div>}
        </div>

        <div className="admin-modal-footer">
          <button type="button" className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={saving || !canSubmit}>
            {saving ? 'Creating…' : 'Create admin'}
          </button>
        </div>
      </form>
    </div>
  );
}
